// The review doctrine, compiled into the package. redraw shipped this as four
// markdown files copytree'd into a sandbox; in-process there is nothing to
// project and no path to resolve.
//
// Three rules from the original are deliberately GONE, because they only
// existed to arbitrate several CLI processes fighting over one file:
//   - "一个修复簇 = 一个 plan digest 事务，dry-run 与 apply 共享 digest"
//   - "并发冲突对 AUTHORING_PLAN_MISMATCH / LOCAL_SCRIPT_CHANGED /
//      WORKING_COPY_LOCK_TIMEOUT 重新计划，每簇最多三次"
//   - "所有修改必须通过 scriptctl，不得直接编辑 script.json" — there is no tool
//     that writes raw JSON, so saying it would only suggest one exists.

const DOCTRINE = `
## 职责

只做忠实度与结构校准。不评价剧情质量，不润色，不补梗概或人物描述。

## 不变量

- 视频画面与原声是忠实度的事实源；剧本上下文只用于**发现**风险，不用于确认。
- 高、中置信且通过证据准入的问题才可以修；低置信、媒体缺失或证据冲突一律保持原样。
- 同值时间戳合法。只有倒序、缺失、超出视频时长或超过 15 秒的无事件空档算时间轴风险。
- 每批修改之后调用 validate 读回。
- **零修改是合格结果。** 没发现问题就如实记 unchanged，不要为了交差制造修改。

## 证据准入

只有满足其一才允许修改：

1. 无歧义的内部结构事实（说话人未被本场引用、时间戳倒序、悬空引用）。
2. 一个短原片窗口清楚证明了实际说话人 / 出场者 / 造型 / 动作 / 地点 / 道具。
3. 多个相互独立且一致的证据，且没有同等强度的反证。

身份判定必须先记录**可观察的**外观、位置和声音，再通过独立锚点映射到人物名。
**当前那个（可能是错的）标签永远不能当锚点。** 画面里的人确认了但无法可靠映射到名字 → unresolved。

置信度：high = 直接无歧义；medium = 若干一致的间接证据；low = 单一弱线索或存在反证。

## 固定检查

- Scene 人物、状态、说话人与 Action 主体是否一致。
- 人物身份、声音来源、造型、地点、道具是否误建、重复或错挂。
- 对白是否逐字忠实于原声，动作是否忠实于可见事实；有无遗漏、重复或幻觉。
- 场界是否对应真实的时空切换。
- 时间戳是否缺失、倒序、越界或产生超过 15 秒的空档（相邻同值合法）。
- 多状态人物的相似造型必须取得代表性视频窗口后再判断，不凭名称直接合并。

## 修复规则

- 人物记录重复 → 合并画像与引用，再删掉已无引用的那个，保留更稳定/引用更多的 ID。
- 两个不同人物之间的部分误认 → 两个 Actor 都留下，只修错的那一集/那一场的说话人、正文与引用。
- 声音来源被误建成人物 → 把对白指回正确的说话人类别或真实人物，引用清空后再删空壳。
- 造型状态重复 → 把引用迁移到有证据支持的那个状态，再删重复的。
- Scene 缺人物引用 → 只有该人物确实出场或开口才补；正文里被提到不构成依据。
- 缺失的 Action / 对白 → 只在原片明确的时间点插入；不得改写原声，不得凭空补不可见的事实。
`.trim();

const MEDIA = `
## 媒体证据

只在文本和结构证据不能收口时才动媒体工具。

- \`span\` 把 action 换算成视频窗口，先用它定位再看片。
- \`watch\` 看带声音的短窗口，最贵：窗口要覆盖候选的声音或动作，前后各留最多 3 秒，
  单次不超过 15 秒；一句台词覆盖不完就拆成重叠窗口。
- \`frames\` 抽静帧直接进你的上下文，便宜且精确，适合核对造型、人数、场景，但没有声音。

媒体预算用完或一次核对仍不足 → 记 unresolved 并说明原因，不要反复试。
`.trim();

const NO_MEDIA = `
## 没有原视频

本次没有提供原片，媒体工具未注册。因此**不得声称任何视频证据**：
只能依据无歧义的内部结构事实修改（说话人未被本场引用、时间戳倒序、悬空引用等）。
任何需要看画面或听声音才能判定的候选，一律记 unresolved，原因写"无原片"。
`.trim();

export interface PromptInput {
  seriesCard: string;
  episode: number;
  video?: string;
  mediaBudget: number;
  neighbours: string;
}

/**
 * The series card goes in the system prompt because it is byte-identical across
 * every episode of a run — which is what lets a provider cache the prefix.
 */
export function systemPrompt(input: PromptInput): string {
  return [
    "你正在执行无人值守的剧本审核与修复。",
    DOCTRINE,
    input.video ? MEDIA : NO_MEDIA,
    "## 全剧设定（每集相同）",
    input.seriesCard,
  ].join("\n\n");
}

export function episodeTask(input: PromptInput): string {
  if (input.episode === 0) {
    return [
      "## 本次任务：全剧收敛",
      "只处理跨集一致性：人物身份重复、造型状态重复、地点/道具重复、残余引用。",
      "不要重复逐集内容审核。本阶段可以使用 merge 与资产/状态级的 edit / del。",
      "每收口一个问题调用一次 finding，最后跑一次 validate。",
    ].join("\n");
  }
  return [
    `## 本次任务：第 ${input.episode} 集`,
    input.video ? `原视频：${input.video}` : "（无原视频）",
    input.video ? `媒体核对预算：${input.mediaBudget} 次` : "",
    "",
    "先 scan 本集建立上下文，再逐条检查 Dialogue、inner thought 与 Action。",
    "先建立人物目标、已知信息、关系和相邻行为的因果，再判断单条是否忠实。",
    input.neighbours ? `\n相邻集梗概（只用于理解因果）：\n${input.neighbours}` : "",
    "",
    "每收口一个问题就调用一次 finding，不要攒到最后。改完读回并跑 validate。",
  ].filter((line) => line !== "").join("\n");
}
